import { extractTrackSegments, findClosestSampleIndex } from './splitTimes';

const FEET_PER_MILE = 5280;
const SEARCH_THRESHOLD_FEET = 200;
const LOOKBACK_FEET = 900;
const LOOKAHEAD_FEET = 600;
const MIN_EVENT_SAMPLES = 3;

const BRAKE_THRESHOLDS = {
  onset: 5,
  active: 10,
  release: 4,
};

const LONG_G_THRESHOLDS = {
  onset: 0.12,
  active: 0.2,
  release: 0.08,
};

function toFeet(miles) {
  return miles * FEET_PER_MILE;
}

function average(values) {
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function spread(values) {
  if (!values.length) return null;
  return Math.max(...values) - Math.min(...values);
}

function gradeFromScore(score) {
  if (score >= 92) return 'A';
  if (score >= 82) return 'B';
  if (score >= 70) return 'C';
  if (score >= 58) return 'D';
  return 'F';
}

function getLapKey(lapLike) {
  if (!lapLike) return 'unknown';
  return lapLike.driverLabel ? `${lapLike.driverLabel}:${lapLike.lapNum}` : `${lapLike.lapNum}`;
}

function averageMetric(items, key) {
  return average(items.map((item) => item?.[key]).filter((value) => Number.isFinite(value)));
}

function inferGBrakeDirection(laps = [], gField = 'longG') {
  let positiveScore = 0;
  let negativeScore = 0;

  laps.forEach((lap) => {
    const samples = lap?.data || [];
    for (let i = 1; i < samples.length; i += 1) {
      const prev = samples[i - 1];
      const curr = samples[i];
      const prevSpeed = Number(prev?.speed);
      const currSpeed = Number(curr?.speed);
      const gValue = Number(curr?.[gField]);

      if (!Number.isFinite(prevSpeed) || !Number.isFinite(currSpeed) || !Number.isFinite(gValue)) continue;

      const speedDrop = prevSpeed - currSpeed;
      if (speedDrop <= 0.1) continue;

      if (gValue >= 0) {
        positiveScore += speedDrop * Math.abs(gValue);
      } else {
        negativeScore += speedDrop * Math.abs(gValue);
      }
    }
  });

  return positiveScore >= negativeScore ? 1 : -1;
}

function hasChannel(laps, field, threshold) {
  return laps.some((lap) => (lap?.data || []).some((sample) => {
    const value = Number(sample?.[field]);
    return Number.isFinite(value) && Math.abs(value) > threshold;
  }));
}

function chooseSource(laps, sourcePreference) {
  if (sourcePreference === 'brake') return 'brake';
  if (sourcePreference === 'lateralG') return 'lateralG';

  if (hasChannel(laps, 'brake', 1)) return 'brake';
  if (hasChannel(laps, 'lateralG', 0.05)) return 'lateralG';
  return 'lateralG';
}

function getSourceMeta(sourceUsed) {
  if (sourceUsed === 'brake') {
    return {
      sourceLabel: 'Brake Pressure',
      peakLabel: 'Peak Brake',
      peakUnit: '%',
      confidence: 'high',
    };
  }

  return {
    sourceLabel: 'Lateral G-Force',
    peakLabel: 'Peak Lat G',
    peakUnit: 'G',
    confidence: 'medium',
  };
}

function getThresholds(sourceUsed) {
  return sourceUsed === 'brake' ? BRAKE_THRESHOLDS : LONG_G_THRESHOLDS;
}

function getBrakeValue(sample, sourceUsed, longGBrakeDirection) {
  if (sourceUsed === 'brake') {
    const brake = Number(sample?.brake);
    return Number.isFinite(brake) ? Math.max(0, brake) : null;
  }

  const gValue = Number(sample?.lateralG);
  if (!Number.isFinite(gValue)) return null;
  return Math.max(0, gValue * longGBrakeDirection);
}

function findWindowStartIndex(samples, startIdx, segmentStartDist) {
  let idx = startIdx;
  while (idx > 0) {
    const distFeet = toFeet(segmentStartDist - (Number(samples[idx - 1]?.dist) || 0));
    if (distFeet > LOOKBACK_FEET) break;
    idx -= 1;
  }
  return idx;
}

function findWindowEndIndex(samples, stopIdx, segmentStopDist) {
  let idx = stopIdx;
  while (idx < samples.length - 1) {
    const distFeet = toFeet((Number(samples[idx + 1]?.dist) || 0) - segmentStopDist);
    if (distFeet > LOOKAHEAD_FEET) break;
    idx += 1;
  }
  return idx;
}

function findPeakIndex(samples, startIdx, endIdx, sourceUsed, longGBrakeDirection) {
  let peakIdx = -1;
  let peakValue = -Infinity;

  for (let i = startIdx; i <= endIdx; i += 1) {
    const value = getBrakeValue(samples[i], sourceUsed, longGBrakeDirection);
    if (!Number.isFinite(value)) continue;
    if (value > peakValue) {
      peakValue = value;
      peakIdx = i;
    }
  }

  return { peakIdx, peakValue };
}

function findEventStartIndex(samples, peakIdx, startIdx, sourceUsed, longGBrakeDirection, threshold) {
  let belowCount = 0;

  for (let i = peakIdx; i >= startIdx; i -= 1) {
    const value = getBrakeValue(samples[i], sourceUsed, longGBrakeDirection) ?? 0;
    if (value <= threshold) {
      belowCount += 1;
      if (belowCount >= 2) {
        return Math.min(peakIdx, i + 1);
      }
    } else {
      belowCount = 0;
    }
  }

  return startIdx;
}

function findEventEndIndex(samples, peakIdx, endIdx, sourceUsed, longGBrakeDirection, threshold) {
  let belowCount = 0;

  for (let i = peakIdx; i <= endIdx; i += 1) {
    const value = getBrakeValue(samples[i], sourceUsed, longGBrakeDirection) ?? 0;
    if (value <= threshold) {
      belowCount += 1;
      if (belowCount >= 2) {
        return Math.max(peakIdx, i - 1);
      }
    } else {
      belowCount = 0;
    }
  }

  return endIdx;
}

function extractMinSpeed(sectionSamples) {
  const validSpeeds = sectionSamples
    .map((sample) => Number(sample?.speed))
    .filter((value) => Number.isFinite(value));

  return validSpeeds.length ? Math.min(...validSpeeds) : null;
}

function buildCornerNote(row) {
  if ((row?.brakeStartSpreadFt ?? 0) >= 20) return 'Braking point moves lap to lap';
  if ((row?.trailBrakeSpreadFt ?? 0) >= 20) return 'Release varies more than onset';
  if ((row?.peakBrakeSpread ?? 0) >= (row?.peakUnit === 'G' ? 0.18 : 14)) return 'Peak brake force is not repeatable';
  if ((row?.score ?? 0) >= 88) return 'Very repeatable braking zone';
  return 'Mostly consistent with small variation';
}

function buildScore({ brakeStartSpreadFt, trailBrakeSpreadFt, peakBrakeSpread, peakUnit }) {
  const startPenalty = Math.min(1, (brakeStartSpreadFt || 0) / 25);
  const trailPenalty = Math.min(1, (trailBrakeSpreadFt || 0) / 25);
  const peakPenalty = peakUnit === 'G'
    ? Math.min(1, (peakBrakeSpread || 0) / 0.25)
    : Math.min(1, (peakBrakeSpread || 0) / 18);

  const penalty = startPenalty * 0.55 + trailPenalty * 0.25 + peakPenalty * 0.20;
  return Math.max(0, Math.min(100, Math.round(100 - penalty * 100)));
}

function summarizeCorner(segment, events, meta) {
  const row = {
    cornerId: segment.label,
    label: segment.label,
    sourceUsed: meta.sourceUsed,
    sourceLabel: meta.sourceLabel,
    peakLabel: meta.peakLabel,
    peakUnit: meta.peakUnit,
    confidence: meta.confidence,
    eventCount: events.length,
    brakeStartAvgFt: averageMetric(events, 'brakeStartFt'),
    brakeStartSpreadFt: spread(events.map((event) => event.brakeStartFt).filter((value) => Number.isFinite(value))),
    peakBrakeAvg: averageMetric(events, 'peakBrake'),
    peakBrakeSpread: spread(events.map((event) => event.peakBrake).filter((value) => Number.isFinite(value))),
    brakeZoneAvgFt: averageMetric(events, 'brakeZoneFt'),
    brakeZoneSpreadFt: spread(events.map((event) => event.brakeZoneFt).filter((value) => Number.isFinite(value))),
    trailBrakeAvgFt: averageMetric(events, 'trailBrakeFt'),
    trailBrakeSpreadFt: spread(events.map((event) => event.trailBrakeFt).filter((value) => Number.isFinite(value))),
    timeToPeakAvgMs: averageMetric(events, 'timeToPeakMs'),
    entrySpeedAvgMph: averageMetric(events, 'entrySpeedMph'),
    minSpeedAvgMph: averageMetric(events, 'minSpeedMph'),
    exitSpeedAvgMph: averageMetric(events, 'exitSpeedMph'),
    fastestLapTimeSec: Math.min(...events.map((event) => event.lapTimeSec).filter((value) => Number.isFinite(value))),
    laps: events,
  };

  row.score = buildScore(row);
  row.grade = gradeFromScore(row.score);
  row.note = buildCornerNote(row);
  return row;
}

export function detectBrakingZones({
  lap,
  segments,
  sourcePreference = 'auto',
  sourceUsed,
  longGBrakeDirection,
  thresholdFeet = SEARCH_THRESHOLD_FEET,
}) {
  const resolvedSource = sourceUsed || chooseSource([lap], sourcePreference);
  const direction = longGBrakeDirection ?? inferGBrakeDirection([lap], 'lateralG');
  const thresholds = getThresholds(resolvedSource);
  const samples = lap?.fullData || lap?.data || [];

  if (!samples.length || !segments?.length) return [];

  return segments.map((segment) => {
    const startMatch = findClosestSampleIndex(samples, segment.startLat, segment.startLng, 0);
    if (startMatch.index < 0 || startMatch.distanceFeet > thresholdFeet) return null;

    const stopMatch = findClosestSampleIndex(samples, segment.stopLat, segment.stopLng, startMatch.index + 1);
    if (stopMatch.index < 0 || stopMatch.distanceFeet > thresholdFeet) return null;

    const segmentStartDist = Number(samples[startMatch.index]?.dist) || 0;
    const segmentStopDist = Number(samples[stopMatch.index]?.dist) || segmentStartDist;

    const windowStart = findWindowStartIndex(samples, startMatch.index, segmentStartDist);
    const windowEnd = findWindowEndIndex(samples, stopMatch.index, segmentStopDist);
    const { peakIdx, peakValue } = findPeakIndex(samples, windowStart, windowEnd, resolvedSource, direction);

    if (peakIdx < 0 || !Number.isFinite(peakValue) || peakValue < thresholds.active) return null;

    const startIdx = findEventStartIndex(samples, peakIdx, windowStart, resolvedSource, direction, thresholds.onset);
    const endIdx = findEventEndIndex(samples, peakIdx, windowEnd, resolvedSource, direction, thresholds.release);

    if (endIdx - startIdx + 1 < MIN_EVENT_SAMPLES) return null;

    const startSample = samples[startIdx];
    const peakSample = samples[peakIdx];
    const endSample = samples[endIdx];
    const segmentEntrySample = samples[startMatch.index];
    const segmentExitSample = samples[stopMatch.index];
    const sectionSamples = samples.slice(startMatch.index, stopMatch.index + 1);
    const startDist = Number(startSample?.dist) || 0;
    const peakDist = Number(peakSample?.dist) || startDist;
    const endDist = Number(endSample?.dist) || peakDist;
    const startTime = Number(startSample?.time) || 0;
    const peakTime = Number(peakSample?.time) || startTime;

    return {
      cornerId: segment.label,
      label: segment.label,
      lapNum: lap?.lapNum ?? null,
      lapKey: getLapKey(lap),
      lapTimeSec: Number(lap?.lapTime),
      driverLabel: lap?.driverLabel || null,
      sourceUsed: resolvedSource,
      brakeStartFt: toFeet(segmentStartDist - startDist),
      peakBrake: peakValue,
      peakBrakeDistFt: Math.max(0, toFeet(segmentStartDist - peakDist)),
      brakeZoneFt: Math.max(0, toFeet(endDist - startDist)),
      trailBrakeFt: Math.max(0, toFeet(endDist - segmentStartDist)),
      timeToPeakMs: Math.max(0, Math.round((peakTime - startTime) * 1000)),
      entrySpeedMph: Number.isFinite(Number(segmentEntrySample?.speed)) ? Number(segmentEntrySample.speed) : null,
      minSpeedMph: extractMinSpeed(sectionSamples),
      exitSpeedMph: Number.isFinite(Number(segmentExitSample?.speed)) ? Number(segmentExitSample.speed) : null,
      startIndex: startIdx,
      peakIndex: peakIdx,
      endIndex: endIdx,
    };
  }).filter(Boolean);
}

export function buildBrakingReferenceSet({
  corners,
  mode = 'best-3',
}) {
  const rows = Array.isArray(corners) ? corners : [];
  const maxEvents = mode === 'best-lap' ? 1 : 3;

  return rows.reduce((acc, corner) => {
    const rankedEvents = (corner?.laps || [])
      .filter((event) => Number.isFinite(event?.lapTimeSec))
      .sort((a, b) => a.lapTimeSec - b.lapTimeSec);

    const selected = rankedEvents.slice(0, maxEvents);
    if (!selected.length) {
      acc[corner.cornerId] = null;
      return acc;
    }

    acc[corner.cornerId] = {
      cornerId: corner.cornerId,
      label: corner.label,
      mode,
      selectedLapCount: selected.length,
      brakeStartAvgFt: averageMetric(selected, 'brakeStartFt'),
      peakBrakeAvg: averageMetric(selected, 'peakBrake'),
      brakeZoneAvgFt: averageMetric(selected, 'brakeZoneFt'),
      trailBrakeAvgFt: averageMetric(selected, 'trailBrakeFt'),
      timeToPeakAvgMs: averageMetric(selected, 'timeToPeakMs'),
      entrySpeedAvgMph: averageMetric(selected, 'entrySpeedMph'),
      minSpeedAvgMph: averageMetric(selected, 'minSpeedMph'),
      exitSpeedAvgMph: averageMetric(selected, 'exitSpeedMph'),
      lapKeys: selected.map((event) => event.lapKey),
    };

    return acc;
  }, {});
}

export function compareCornerToReference({
  corner,
  referenceCorner,
}) {
  if (!corner || !referenceCorner) return null;

  return {
    brakeStartDeltaFt: (corner.brakeStartAvgFt ?? 0) - (referenceCorner.brakeStartAvgFt ?? 0),
    peakBrakeDelta: (corner.peakBrakeAvg ?? 0) - (referenceCorner.peakBrakeAvg ?? 0),
    brakeZoneDeltaFt: (corner.brakeZoneAvgFt ?? 0) - (referenceCorner.brakeZoneAvgFt ?? 0),
    trailBrakeDeltaFt: (corner.trailBrakeAvgFt ?? 0) - (referenceCorner.trailBrakeAvgFt ?? 0),
    timeToPeakDeltaMs: (corner.timeToPeakAvgMs ?? 0) - (referenceCorner.timeToPeakAvgMs ?? 0),
    entrySpeedDeltaMph: (corner.entrySpeedAvgMph ?? 0) - (referenceCorner.entrySpeedAvgMph ?? 0),
    minSpeedDeltaMph: (corner.minSpeedAvgMph ?? 0) - (referenceCorner.minSpeedAvgMph ?? 0),
    exitSpeedDeltaMph: (corner.exitSpeedAvgMph ?? 0) - (referenceCorner.exitSpeedAvgMph ?? 0),
  };
}

export function analyzeBraking({
  laps,
  trackCoordinates,
  distanceUnit = 'ft',
  sourcePreference = 'auto',
}) {
  const safeLaps = Array.isArray(laps) ? laps.filter((lap) => lap?.data?.length) : [];
  const segments = extractTrackSegments(trackCoordinates);

  if (!safeLaps.length || !segments.length) {
    return {
      sourceUsed: null,
      sourceLabel: null,
      peakLabel: null,
      peakUnit: null,
      confidence: 'low',
      distanceUnit,
      referenceMode: 'best-3',
      referenceLabel: 'Best 3 laps',
      sessionSummary: null,
      referenceSets: { bestLap: {}, best3: {} },
      corners: [],
    };
  }

  const sourceUsed = chooseSource(safeLaps, sourcePreference);
  const longGBrakeDirection = inferGBrakeDirection(safeLaps, 'lateralG');
  const meta = { ...getSourceMeta(sourceUsed), sourceUsed };
  const eventsByCorner = new Map();

  safeLaps.forEach((lap) => {
    detectBrakingZones({
      lap,
      segments,
      sourceUsed,
      longGBrakeDirection,
      sourcePreference,
    }).forEach((event) => {
      const key = event.cornerId;
      if (!eventsByCorner.has(key)) eventsByCorner.set(key, []);
      eventsByCorner.get(key).push(event);
    });
  });

  const rawCorners = segments
    .map((segment) => {
      const events = eventsByCorner.get(segment.label) || [];
      if (!events.length) return null;
      return summarizeCorner(segment, events, meta);
    })
    .filter(Boolean);

  if (!rawCorners.length) {
    return {
      sourceUsed,
      sourceLabel: meta.sourceLabel,
      peakLabel: meta.peakLabel,
      peakUnit: meta.peakUnit,
      confidence: meta.confidence,
      distanceUnit,
      referenceMode: 'best-3',
      referenceLabel: 'Best 3 laps',
      sessionSummary: null,
      referenceSets: { bestLap: {}, best3: {} },
      corners: [],
    };
  }

  const bestLapReferenceSet = buildBrakingReferenceSet({ corners: rawCorners, mode: 'best-lap' });
  const best3ReferenceSet = buildBrakingReferenceSet({ corners: rawCorners, mode: 'best-3' });

  const corners = rawCorners.map((corner) => {
    const bestLapReference = bestLapReferenceSet[corner.cornerId] || null;
    const best3Reference = best3ReferenceSet[corner.cornerId] || null;

    return {
      ...corner,
      bestLapReference,
      best3Reference,
      bestLapDelta: compareCornerToReference({ corner, referenceCorner: bestLapReference }),
      best3Delta: compareCornerToReference({ corner, referenceCorner: best3Reference }),
    };
  });

  const worstCorner = corners.reduce((worst, corner) => (
    !worst || (corner.brakeStartSpreadFt ?? 0) > (worst.brakeStartSpreadFt ?? 0) ? corner : worst
  ), null);

  const bestCorner = corners.reduce((best, corner) => (
    !best || (corner.score ?? 0) > (best.score ?? 0) ? corner : best
  ), null);

  return {
    sourceUsed,
    sourceLabel: meta.sourceLabel,
    peakLabel: meta.peakLabel,
    peakUnit: meta.peakUnit,
    confidence: meta.confidence,
    distanceUnit,
    referenceMode: 'best-3',
    referenceLabel: 'Best 3 laps',
    referenceSets: {
      bestLap: bestLapReferenceSet,
      best3: best3ReferenceSet,
    },
    sessionSummary: {
      worstCornerId: worstCorner?.cornerId ?? null,
      worstCornerLabel: worstCorner?.label ?? null,
      maxStartSpread: worstCorner?.brakeStartSpreadFt ?? null,
      bestCornerId: bestCorner?.cornerId ?? null,
      bestCornerLabel: bestCorner?.label ?? null,
      avgStartSpread: average(corners.map((corner) => corner.brakeStartSpreadFt).filter((value) => value != null)),
    },
    corners,
  };
}
